<div class="filter">
    <mat-form-field class="filter__search" appearance="outline">
        <mat-label>Søg i {{ filterText }}</mat-label>
        <input
            #filter
            matInput
            (keyup)="filterItems(filter.value)"
            [attr.aria-label]="'Søg i ' + filterText"
            [attr.aria-describedby]="'search-description'"
            [attr.data-testid]="searchTestId"
            autocomplete="off"
            spellcheck="false"
            id="filter-input-felt"
            [attr.data-testid]="'filter-input-felt'"
        />
        <mat-icon class="material-symbols-outlined" aria-hidden="true">search</mat-icon>
        <span id="search-description" class="sr-only">Søg i {{ filterText }}</span>
    </mat-form-field>
</div>

<div class="filter-container mt-2 mb-4" role="group" aria-label="Filtre">
    @if (businessAreas && businessAreas.length > 0) {
        <mat-chip-listbox
            selectable
            multiple
            id="arbejdsomraader-filter"
            [attr.data-testid]="'arbejdsomraader-filter'"
            aria-label="Arbejdsområde filtre"
            class="chip-filter-set"
        >
            @for (area of businessAreas; track area.id) {
                @if (area.count && area.count > 0) {
                    <mat-chip-option
                        class="filter-chip"
                        (selectionChange)="toggleFilterArea(area)"
                        tabindex="0"
                        [attr.aria-label]="'Filter for ' + area.name + ' (' + area.count + ')'"
                        [attr.data-businessareaid-value]="area.id"
                    >
                        {{ area.name }} ({{ area.count }})
                    </mat-chip-option>
                }
            }
        </mat-chip-listbox>
    }

    @if (type === FilterType.Case || type === FilterType.Message) {
        <button
            mat-flat-button
            class="cta-button filter__toggle-filter-button"
            (click)="toggleFilters()"
            [attr.aria-label]="showExtendedFilters ? 'Luk udvidet filtrering' : 'Åben udvidet filtrering'"
            type="button"
            role="button"
            id="filter-expand-button"
            [attr.data-testid]="'filter-expand-button'"
        >
            <mat-icon class="material-symbols-outlined" aria-hidden="true">filter_alt</mat-icon>
            <span>Filtrering</span>
        </button>
    }

    @if (type === FilterType.Course) {
        <div class="filter-extra-actions">
            <ng-content select="[filterActions]"></ng-content>
        </div>
    }
</div>

@if (showExtendedFilters) {
    <div class="filter-extended-wrapper" aria-label="Udvidede filtre" role="region">
        @if (contactCases && contactCases.length > 0) {
            <mat-form-field appearance="outline" class="filter__contact-select" aria-label="Filtrer efter opretter">
                <mat-label id="oprettet-af-label">{{ getDictionaryItem("reportedBy") }}</mat-label>
                <mat-select
                    id="oprettet-af-select"
                    [attr.data-testid]="'oprettet-af-select'"
                    [multiple]="true"
                    [value]="selectedContactIds"
                    (selectionChange)="onContactSelectionChange($event.value)"
                    aria-labelledby="oprettet-af-label"
                    aria-label="Vælg kontaktperson"
                >
                    @for (contact of contactCases; track contact.contactId) {
                        <mat-option [value]="contact.contactId" [attr.data-contactid-filter-value]="contact.contactId">
                            {{ contact.name }}
                        </mat-option>
                    }
                </mat-select>
            </mat-form-field>
        }

        @if (caseStatuses && caseStatuses.length > 0) {
            <mat-form-field appearance="outline" class="filter__contact-select" aria-label="Filtrer efter status">
                <mat-label id="status-label">{{ getDictionaryItem("status") }}</mat-label>
                <mat-select
                    id="status-select"
                    [attr.data-testid]="'status-select'"
                    [multiple]="true"
                    [value]="selectedStatusCodes"
                    (selectionChange)="onStatuscodeSelectionChange($event.value)"
                    aria-labelledby="status-label"
                    aria-label="Vælg status"
                >
                    @for (statuses of caseStatuses; track statuses.code) {
                        <mat-option [value]="statuses.code" [attr.data-statuscode-filter-value]="statuses.code">
                            {{ statuses.name }}
                        </mat-option>
                    }
                </mat-select>
            </mat-form-field>
        }

        <mat-form-field aria-label="Filtrer efter oprettelsesdato">
            <mat-label id="oprettelsesdato-label">Oprettelsesdato</mat-label>
            <mat-date-range-input [rangePicker]="picker" [formGroup]="dateRange" aria-labelledby="oprettelsesdato-label">
                <input
                    matStartDate
                    placeholder="Dato fra"
                    formControlName="start"
                    (dateChange)="emitFilterChanged()"
                    aria-label="Dato fra"
                    [attr.data-testid]="'oprettelsesdato-start'"
                />
                <input
                    matEndDate
                    placeholder="Dato til"
                    formControlName="end"
                    (dateChange)="emitFilterChanged()"
                    aria-label="Dato til"
                    [attr.data-testid]="'oprettelsesdato-end'"
                />
            </mat-date-range-input>
            <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-date-range-picker #picker>
                <mat-date-range-picker-actions>
                    <button mat-button matButton="elevated" matDateRangePickerCancel aria-label="Ryd dato filter" type="button" (click)="clearDateRange()">
                        {{ getDictionaryItem("clearDateRange") }}
                    </button>
                    <button mat-button matButton="elevated" matDateRangePickerApply aria-label="Tilføj dato filter" type="button">Tilføj dato filter</button>
                </mat-date-range-picker-actions>
            </mat-date-range-picker>
        </mat-form-field>
    </div>
    @if (selectedContactIds.length || selectedStatusCodes.length || dateRange.value.start || dateRange.value.end) {
        <div class="selected-filters" aria-label="Valgte filtre" role="region">
            <span>{{ getDictionaryItem("chosenFilters") }}:</span>
            @for (contactId of selectedContactIds; track contactId) {
                <button
                    mat-stroked-button
                    color="primary"
                    class="filter-chip"
                    (click)="removeContactFilter(contactId)"
                    [attr.aria-label]="'Fjern filter for ' + getContactName(contactId)"
                    type="button"
                >
                    {{ getContactName(contactId) }}
                    <mat-icon class="material-symbols-outlined icon" aria-hidden="true">close</mat-icon>
                </button>
            }
            @for (statusCode of selectedStatusCodes; track statusCode) {
                <button
                    mat-stroked-button
                    color="primary"
                    class="filter-chip"
                    (click)="removeStatusFilter(statusCode)"
                    [attr.aria-label]="'Fjern filter for ' + getStatusName(statusCode)"
                    type="button"
                >
                    {{ getStatusName(statusCode) }}
                    <mat-icon class="material-symbols-outlined icon" aria-hidden="true">close</mat-icon>
                </button>
            }
            @if (dateRange.value.start) {
                <button mat-stroked-button color="primary" class="filter-chip" (click)="clearDateRange()" aria-label="Fjern filter for oprettelsesdato" type="button">
                    Fra: {{ dateRange.value.start | date: "dd-MM-yyyy" }} {{ dateRange.value.end ? "Til: " + (dateRange.value.end | date: "dd-MM-yyyy") : "" }}
                    <mat-icon class="material-symbols-outlined icon" aria-hidden="true">close</mat-icon>
                </button>
            }
            @if (selectedContactIds.length + selectedStatusCodes.length + (dateRange.value.start ? 1 : 0) + (dateRange.value.end && !dateRange.value.start ? 1 : 0) > 1) {
                <button
                    mat-stroked-button
                    color="warn"
                    class="filter-chip"
                    (click)="removeAllFilters()"
                    aria-label="Ryd alle filtre"
                    type="button"
                    id="clear-all-filters-button"
                    [attr.data-testid]="'clear-all-filters-button'"
                >
                    {{ getDictionaryItem("clearAllFilters") }}
                    <mat-icon class="material-symbols-outlined icon" aria-hidden="true">close</mat-icon>
                </button>
            }
        </div>
    }
}
